import { ReactNode } from 'react';
import {
    DialogTitle,
    DialogTitleProps,
    IconButton,
    IconButtonProps,
    ButtonProps,
    Theme,
    Typography,
    Box,
    Button,
    BoxProps,
} from '@mui/material';

import { useFullScreenDialog } from '../FullScreenDialogContext';
import {
    HorizontalProgressBar,
    type HorizontalProgressBarProps,
    ProgressBarLeftElement,
    type ProgressBarLeftElementProps,
} from '../ProgressBar';

import CollectiveIcon from 'modules/common/components/Icons/CollectiveIcon';
import CloseXIcon from 'modules/common/components/Icons/CloseXIcon';
import useAuthSelectors from 'modules/Auth/AuthProvider/useAuthSelector';
import StopImpersonatingButton from 'modules/common/components/MUI/FullScreenDialog/FullScreenDialogHeader/StopImpersonatingButton';
import CollectiveLogoBlack from 'assets/img/collective_logo_black.svg';
import mergeSx from 'modules/common/utils/mergeSx';

type CloseButtonProps =
    | ({
          label?: string;
          asIcon: false;
      } & ButtonProps)
    | ({
          asIcon: true;
          icon?: ReactNode;
      } & IconButtonProps);

/**
 * Props for FullScreenDialogHeader component
 */
export interface FullScreenDialogHeaderProps extends Omit<DialogTitleProps, 'children'> {
    /**
     * Title of the dialog
     */
    title?: string;
    /**
     * Element to render on the left side of the header
     */
    leftElement?: ReactNode;

    /**
     * Element to render on the right side of the header
     */
    rightElement?: ReactNode;

    /**
     * Whether to hide the close button
     */
    noClose?: boolean;

    /**
     * Optional logo element to display in the center of the header
     */
    showLogo?: boolean;

    /**
     * Whether to show the full collective logo or just the icon
     */
    logoVariant?: 'icon' | 'full';

    slotProps?: {
        logo?: BoxProps;
        title?: BoxProps;
        rightElement?: BoxProps;
        /** Styling for the DialogTitle root — e.g. constraining the header to a maxWidth band. */
        root?: Pick<DialogTitleProps, 'sx'>;
    };

    /**
     * Optional props for the horizontal progress bar
     */
    horizontalProgressBarProps?: HorizontalProgressBarProps;

    /**
     * Optional props for the progress bar left element navigation
     */
    progressBarLeftElementProps?: ProgressBarLeftElementProps;

    /**
     * Whether to force display the close button
     */
    forceDisplayCloseButton?: boolean;

    /**
     * Optional props for the close button
     */
    closeButtonProps?: CloseButtonProps;
}

function CloseButton({ asIcon = true, ...props }: CloseButtonProps) {
    const { close } = useFullScreenDialog();

    if (!asIcon) {
        const { label = 'Close', ...buttonProps } = props as { label: string };
        return (
            <Button onClick={close} color="secondary" variant="text" {...buttonProps}>
                {label}
            </Button>
        );
    }

    const { icon, ...iconButtonProps } = props as { icon: ReactNode };

    return (
        <IconButton onClick={close} color="secondary" {...iconButtonProps}>
            {icon || <CloseXIcon />}
        </IconButton>
    );
}

function Title({ title }: { title?: string }) {
    return (
        <Typography variant="subtitle1" component="span">
            {title}
        </Typography>
    );
}

export default function FullScreenDialogHeader({
    title,
    rightElement,
    leftElement,
    showLogo = false,
    logoVariant = 'icon',
    noClose = false,
    forceDisplayCloseButton = false,
    horizontalProgressBarProps,
    progressBarLeftElementProps,
    closeButtonProps,
    slotProps = {},
    ...dialogTitleProps
}: Readonly<FullScreenDialogHeaderProps>) {
    const { isScrolled } = useFullScreenDialog();
    const { isImpersonating } = useAuthSelectors('isImpersonating');

    const finalLeftElement =
        leftElement ||
        (horizontalProgressBarProps && progressBarLeftElementProps ? (
            <ProgressBarLeftElement {...progressBarLeftElementProps} />
        ) : undefined);

    return (
        <>
            <DialogTitle
                {...dialogTitleProps}
                sx={mergeSx(
                    {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderBottom: (theme: Theme) => (isScrolled ? `1px solid ${theme.palette.grey[100]}` : 'none'),
                        position: 'relative',
                        ...(isImpersonating && { bgcolor: 'red.700', color: 'white' }),
                    },
                    dialogTitleProps.sx,
                    slotProps?.root?.sx
                )}>
                <Box
                    {...slotProps?.title}
                    sx={{
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        flex: 1,
                        ...slotProps?.title?.sx,
                    }}>
                    {finalLeftElement || <Title title={title} />}
                </Box>
                {showLogo && (
                    <Box
                        {...slotProps?.logo}
                        sx={{
                            position: 'absolute',
                            left: '50%',
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 'auto',
                            ...slotProps?.logo?.sx,
                        }}>
                        {logoVariant === 'icon' && <CollectiveIcon sx={{ fontSize: '2.75rem' }} />}
                        {logoVariant === 'full' && (
                            <Box component="img" src={CollectiveLogoBlack} alt="Collective" height={40} />
                        )}
                    </Box>
                )}
                <Box
                    {...slotProps?.rightElement}
                    sx={{
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                        flex: 1,
                        justifyContent: 'flex-end',
                        flexWrap: 'wrap',
                        gap: 1,
                        ...slotProps?.rightElement?.sx,
                    }}>
                    {isImpersonating && <StopImpersonatingButton />}
                    {rightElement || (!noClose && <CloseButton asIcon {...closeButtonProps} />)}
                    {(rightElement || !noClose) && forceDisplayCloseButton && (
                        <CloseButton asIcon {...closeButtonProps} />
                    )}
                </Box>
            </DialogTitle>
            {horizontalProgressBarProps && <HorizontalProgressBar {...horizontalProgressBarProps} />}
        </>
    );
}
